import { Injectable } from '@angular/core';
import { StatusCodeInfo } from '../../models/dynamic.model';
import {
  BehaviorSubject,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  tap,
} from 'rxjs';
import {
  ManifestPodPreview,
  ManifestPodSnapshot,
} from 'src/app/features/model/hawb.model';

export interface StatusCodeFormObject extends StatusCodeInfo {
  isInputRow?: boolean;
  attachments: string;
  additionalDetails: string;
  tempId: string;
  dateTimeAndTimezone?: unknown;
  /** Manifest POD UI-only row state (SPEC-BPM-001) — never sent to the API. */
  podPreview?: ManifestPodPreview | null;
  selectedHawbIds?: number[] | null;
  podSnapshot?: ManifestPodSnapshot;
  shipmentsLinked?: string;
}

export const STATUS_CODE_UI_ONLY_KEYS = [
  'dateTimeAndTimezone',
  'tempId',
  'isInputRow',
  'podPreview',
  'selectedHawbIds',
  'podSnapshot',
  'shipmentsLinked',
] as const;

@Injectable({ providedIn: 'root' })
export class StatusCodeService {
  statusCodesBehaviorSubject = new BehaviorSubject<StatusCodeFormObject[]>([]);
  /** Manifest POD snapshots by status-code row id (D-BPM-UI-008) — one GET per row. */
  private readonly manifestPodSnapshots = new Map<number, ManifestPodSnapshot>();
  /** In-flight snapshot GETs, shared so a re-created tab joins the request instead of dropping it. */
  private readonly manifestPodSnapshotRequests = new Map<
    number,
    Observable<ManifestPodSnapshot>
  >();

  get statusCodeInfoCollection$(): Observable<StatusCodeFormObject[]> {
    return this.statusCodesBehaviorSubject.asObservable().pipe(
      map(infoObjects => infoObjects.filter(info => info.action !== 'd'))
    );
  }

  get statusCodeInfoCollection(): StatusCodeInfo[] {
    return this.statusCodesBehaviorSubject
      .getValue()
      .filter(info => info.id !== 0 || info.typeCodeId !== 0)
      .map(row => {
        const clone = structuredClone(row);
        STATUS_CODE_UI_ONLY_KEYS.forEach(key => {
          delete clone[key];
        });
        if (!clone.id) {
          clone.id = 0;
          clone.action = 'a';
        } else if (clone.action === 'c') {
          clone.action = 'u';
        }

        return clone;
      });
  }

  getAllStatusCodes(): StatusCodeFormObject[] {
    return this.statusCodesBehaviorSubject.getValue();
  }

  get isInputRowAdded(): boolean {
    return this.statusCodesBehaviorSubject.getValue().some(x => x.isInputRow);
  }

  getManifestPodSnapshot(id: number): ManifestPodSnapshot | undefined {
    return this.manifestPodSnapshots.get(id);
  }

  setManifestPodSnapshot(id: number, snapshot: ManifestPodSnapshot): void {
    this.manifestPodSnapshots.set(id, snapshot);
  }

  /**
   * D-BPM-UI-008: one snapshot GET per row. The request is not tied to the component that
   * started it: the Manifest page re-creates the Status Codes tab while loading, and a
   * cancelled GET used to leave the row without its `View XX linked shipments` link.
   */
  loadManifestPodSnapshot(
    id: number,
    request: () => Observable<ManifestPodSnapshot>
  ): Observable<ManifestPodSnapshot> {
    const cached = this.manifestPodSnapshots.get(id);
    if (cached) {
      return of(cached);
    }
    const pending = this.manifestPodSnapshotRequests.get(id);
    if (pending) {
      return pending;
    }
    const inFlight: Observable<ManifestPodSnapshot> = request().pipe(
      tap(snapshot => this.manifestPodSnapshots.set(id, snapshot)),
      // A failed GET is dropped so the next tab load retries it.
      finalize(() => {
        if (this.manifestPodSnapshotRequests.get(id) === inFlight) {
          this.manifestPodSnapshotRequests.delete(id);
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.manifestPodSnapshotRequests.set(id, inFlight);
    return inFlight;
  }

  setCollection(collection: StatusCodeFormObject[]) {
    this.manifestPodSnapshots.clear();
    this.manifestPodSnapshotRequests.clear();
    this.statusCodesBehaviorSubject.next(
      collection.map(x => ({ ...x, tempId: x.id.toString() }))
    );
  }

  add(info: StatusCodeFormObject) {
    const val = this.statusCodesBehaviorSubject.getValue();
    this.statusCodesBehaviorSubject.next([info, ...val]);
  }

  remove(id: number | string) {
    const val = this.statusCodesBehaviorSubject.getValue();

    this.statusCodesBehaviorSubject.next(
      val.map(x => {
        if (x.id !== id || x.tempId !== id.toString()) {
          x.action = 'd';
        }

        return structuredClone(x);
      })
    );
  }

  triggerEditMode(id: number | string, inputRow = true) {
    const val = inputRow
      ? this.statusCodesBehaviorSubject.getValue().filter(x => !x.isInputRow)
      : this.statusCodesBehaviorSubject.getValue();
    const index = val.findIndex(x => x.id == id || x.tempId == id);
    if (index >= 0) {
      val[index].isInputRow = inputRow;
      this.statusCodesBehaviorSubject.next(val);
    }
  }

  removeBulk(id: string[]) {
    const val = this.statusCodesBehaviorSubject.getValue();
    this.statusCodesBehaviorSubject.next(
      val.map(x => {
        if (id.includes(x.tempId) || id.includes(x.id.toString())) {
          x.action = 'd';
        }

        return structuredClone(x);
      })
    );
  }

  update(id: number | string, info: StatusCodeFormObject) {
    const val = this.statusCodesBehaviorSubject.getValue();
    const index = val.findIndex(x => x.id === id || x.tempId === id);
    if (index >= 0) {
      val[index] = {
        ...val[index],
        ...info,
      };
      this.statusCodesBehaviorSubject.next(val);
    }
  }

  removeInputRowByTempId(tempId: string) {
    const val = this.statusCodesBehaviorSubject.getValue();
    const updated = val.filter(x => x.tempId !== tempId);
    this.statusCodesBehaviorSubject.next(updated);
  }

  public filterAndTransform(list: any[]): any[] {
    if (!Array.isArray(list) || list.length === 0) return [];

    const groupedById = new Map<number, any[]>();
    list.forEach(item => {
      const group = groupedById.get(item.id) || [];
      group.push(item);
      groupedById.set(item.id, group);
    });

    const filteredList: any[] = [];

    groupedById.forEach((items:any) => {
      const sorted = items.sort((a:any, b:any) => {
        const aDate = new Date(a.date || a.dateTime).getTime();
        const bDate = new Date(b.date || b.dateTime).getTime();

        if (bDate !== aDate) return bDate - aDate;
        if (b.action === 'u' && a.action !== 'u') return 1;
        if (a.action === 'u' && b.action !== 'u') return -1;
        return 0;
      });

      const selected = sorted[0];
      selected.action = 'u'; // or CrudActions.Update
      selected.statusCodeAttachments?.attachmentList?.forEach((att: any) => {
        att.action = 'u';
      });

      filteredList.push(selected);
    });

    return filteredList;
  }
}
